/**
 * PLANS & PRICING CONFIGURATION
 *
 * Configure subscription plans, pricing, and quotas here.
 * This is the SINGLE SOURCE OF TRUTH for all pricing.
 */

const plansConfig = {
  // ==========================================
  // SUBSCRIPTION PLANS
  // ==========================================
  plans: [
    {
      id: 'free',
      name: 'FREE',
      displayName: 'Free Plan',
      description: 'Perfect for trying out the service',
      price: 0,
      currency: 'EUR',
      billingPeriod: 'month',

      // Quota configuration
      quota: {
        limit: 100,                    // 100 analyses per month
        resetPeriod: 'monthly',        // monthly, yearly, never
      },

      // Features included
      features: [
        '100 analyses per month',
        'Basic support',
        'Email notifications',
      ],

      // Feature flags
      flags: {
        prioritySupport: false,
        apiAccess: false,
        exportData: false,
        advancedAnalytics: false,
      },

      // Display options
      display: {
        badge: null,                   // 'Popular', 'Best Value', etc.
        color: '#718096',              // Gray
        highlighted: false,
        order: 1,
      },
    },

    {
      id: 'starter',
      name: 'STARTER',
      displayName: 'Starter Plan',
      description: 'For small teams and regular users',
      price: 9.99,
      currency: 'EUR',
      billingPeriod: 'month',

      quota: {
        limit: 1000,                   // 1,000 analyses per month
        resetPeriod: 'monthly',
      },

      features: [
        '1,000 analyses per month',
        'Priority email support',
        'API access',
        'Export data (CSV)',
        'Email notifications',
      ],

      flags: {
        prioritySupport: true,
        apiAccess: true,
        exportData: true,
        advancedAnalytics: false,
      },

      display: {
        badge: 'Popular',
        color: '#667eea',              // Purple
        highlighted: true,
        order: 2,
      },
    },

    {
      id: 'pro',
      name: 'PRO',
      displayName: 'Pro Plan',
      description: 'For power users and growing businesses',
      price: 49.99,
      currency: 'EUR',
      billingPeriod: 'month',

      quota: {
        limit: 10000,                  // 10,000 analyses per month
        resetPeriod: 'monthly',
      },

      features: [
        '10,000 analyses per month',
        '24/7 priority support',
        'Full API access',
        'Export data (CSV, JSON, Excel)',
        'Advanced analytics',
        'Custom integrations',
        'Dedicated account manager',
      ],

      flags: {
        prioritySupport: true,
        apiAccess: true,
        exportData: true,
        advancedAnalytics: true,
      },

      display: {
        badge: 'Best Value',
        color: '#f6ad55',              // Orange/Gold
        highlighted: false,
        order: 3,
      },
    },

    // {
    //   id: 'enterprise',
    //   name: 'ENTERPRISE',
    //   displayName: 'Enterprise Plan',
    //   description: 'Custom solutions for large organizations',
    //   price: null,                   // Custom pricing
    //   currency: 'EUR',
    //   billingPeriod: 'month',
    //
    //   quota: {
    //     limit: -1,                   // Unlimited (-1 means unlimited)
    //     resetPeriod: 'monthly',
    //   },
    //
    //   features: [
    //     'Unlimited analyses',
    //     '24/7 dedicated support',
    //     'Custom API integrations',
    //     'On-premise deployment option',
    //     'SLA guarantee',
    //     'Custom training',
    //   ],
    //
    //   flags: {
    //     prioritySupport: true,
    //     apiAccess: true,
    //     exportData: true,
    //     advancedAnalytics: true,
    //     customIntegrations: true,
    //   },
    //
    //   display: {
    //     badge: 'Enterprise',
    //     color: '#2d3748',
    //     highlighted: false,
    //     order: 4,
    //     ctaText: 'Contact Sales',    // Custom CTA
    //   },
    // },
  ],

  // ==========================================
  // PRICING SETTINGS
  // ==========================================
  pricing: {
    // Default plan for new users
    defaultPlan: 'free',

    // Allow trials?
    trial: {
      enabled: false,
      durationDays: 14,
      planId: 'pro',                   // Which plan to trial
    },

    // Discounts
    discounts: {
      // Annual billing discount
      annualDiscount: 20,              // 20% off if billed annually

      // Promo codes (optional)
      promoCodes: {
        'LAUNCH50': {
          type: 'percentage',          // 'percentage' or 'fixed'
          value: 50,                   // 50% off
          plans: ['starter', 'pro'],   // Applicable plans
          expiresAt: '2026-12-31',
        },
        // 'WELCOME10': {
        //   type: 'fixed',
        //   value: 10,                 // 10 EUR off
        //   plans: ['starter'],
        //   expiresAt: '2026-06-30',
        // },
      },
    },

    // Tax settings
    tax: {
      enabled: false,
      rate: 20,                        // 20% VAT (example)
      included: true,                  // Price includes tax?
    },
  },

  // ==========================================
  // QUOTA MANAGEMENT
  // ==========================================
  quotaSettings: {
    // Overage policy
    overage: {
      allowed: false,                  // Allow usage beyond quota?
      chargePerUnit: 0.10,            // Charge 0.10 EUR per extra analysis
      maxOverage: 100,                // Max 100 extra analyses
    },

    // Grace period when quota exceeded
    gracePeriod: {
      enabled: false,
      analyses: 10,                    // Allow 10 extra analyses
      message: 'You have exceeded your quota. These are courtesy analyses.',
    },

    // Rollover unused quota?
    rollover: {
      enabled: false,
      maxRollover: 500,               // Max 500 analyses can rollover
    },
  },

  // ==========================================
  // PAYMENT SETTINGS
  // ==========================================
  payment: {
    // Payment providers
    providers: {
      paypal: {
        enabled: true,
        mode: 'sandbox',               // 'sandbox' or 'live'
      },
      stripe: {
        enabled: false,
        mode: 'test',
      },
    },

    // Billing cycles available
    billingCycles: ['monthly'],        // ['monthly', 'yearly']

    // Auto-renewal
    autoRenewal: {
      enabled: true,
      notifyDaysBefore: 7,            // Notify 7 days before renewal
    },

    // Refund policy
    refund: {
      enabled: true,
      periodDays: 30,                 // 30-day money-back guarantee
    },
  },

  // ==========================================
  // DISPLAY & UI
  // ==========================================
  display: {
    // Currency display
    currencySymbol: '€',
    currencyPosition: 'after',         // 'before' ($9.99) or 'after' (9.99€)
    decimalPlaces: 2,

    // Show comparison table?
    showComparisonTable: true,

    // Show FAQ on pricing page?
    showFAQ: true,

    // Testimonials
    showTestimonials: false,
  },
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Get all plans
 */
export const getAllPlans = () => {
  return plansConfig.plans.sort((a, b) => a.display.order - b.display.order);
};

/**
 * Get a specific plan by ID
 */
export const getPlanById = (planId) => {
  return plansConfig.plans.find(plan => plan.id === planId) || null;
};

/**
 * Get default plan
 */
export const getDefaultPlan = () => {
  return getPlanById(plansConfig.pricing.defaultPlan);
};

/**
 * Get plan price with optional discount
 */
export const getPlanPrice = (planId, billing = 'monthly', promoCode = null) => {
  const plan = getPlanById(planId);
  if (!plan) return null;

  let price = plan.price;

  // Apply annual discount
  if (billing === 'yearly' && plansConfig.pricing.discounts.annualDiscount) {
    const discount = plansConfig.pricing.discounts.annualDiscount / 100;
    price = price * 12 * (1 - discount);
  } else if (billing === 'yearly') {
    price = price * 12;
  }

  // Apply promo code
  if (promoCode && plansConfig.pricing.discounts.promoCodes[promoCode]) {
    const promo = plansConfig.pricing.discounts.promoCodes[promoCode];

    // Check if valid for this plan
    if (promo.plans.includes(planId)) {
      // Check expiration
      if (!promo.expiresAt || new Date(promo.expiresAt) > new Date()) {
        if (promo.type === 'percentage') {
          price = price * (1 - promo.value / 100);
        } else if (promo.type === 'fixed') {
          price = Math.max(0, price - promo.value);
        }
      }
    }
  }

  return price;
};

/**
 * Format price for display
 */
export const formatPrice = (price) => {
  const { currencySymbol, currencyPosition, decimalPlaces } = plansConfig.display;
  const formatted = price.toFixed(decimalPlaces);

  return currencyPosition === 'before'
    ? `${currencySymbol}${formatted}`
    : `${formatted}${currencySymbol}`;
};

/**
 * Get quota for plan
 */
export const getQuotaForPlan = (planId) => {
  const plan = getPlanById(planId);
  return plan ? plan.quota : null;
};

/**
 * Check if plan has feature
 */
export const planHasFeature = (planId, featureName) => {
  const plan = getPlanById(planId);
  return plan ? plan.flags[featureName] === true : false;
};

/**
 * Get pricing settings
 */
export const getPricingSettings = () => plansConfig.pricing;

/**
 * Get quota settings
 */
export const getQuotaSettings = () => plansConfig.quotaSettings;

/**
 * Get payment settings
 */
export const getPaymentSettings = () => plansConfig.payment;

/**
 * Get display settings
 */
export const getDisplaySettings = () => plansConfig.display;

/**
 * Validate promo code
 */
export const validatePromoCode = (code, planId) => {
  const promo = plansConfig.pricing.discounts.promoCodes[code];

  if (!promo) {
    return { valid: false, message: 'Invalid promo code' };
  }

  // Check plan eligibility
  if (!promo.plans.includes(planId)) {
    return { valid: false, message: 'This promo code is not valid for this plan' };
  }

  // Check expiration
  if (promo.expiresAt && new Date(promo.expiresAt) < new Date()) {
    return { valid: false, message: 'This promo code has expired' };
  }

  return {
    valid: true,
    discount: promo.value,
    type: promo.type,
  };
};

export default plansConfig;
